import React, { useEffect } from 'react';
import { Descriptions, Typography, Divider, Skeleton, Flex, Card, Tooltip } from 'antd';
import Icon from '@mdi/react';
import {
  mdiMagnify,
  mdiCellphone,
  mdiIpNetwork,
  mdiTabletCellphone,
  mdiWeb,
  mdiRobotOutline,
  mdiClockOutline,
  mdiChartBar,
  mdiAccountCogOutline,
} from '@mdi/js';
import useSearchParams from 'hooks/useSearchParams';
import ninjaApi from 'store/redux/apis';
import JourneyUrlFormatter from 'modules/ReportModule/components/JourneyUrlFormatter';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import SendEventConversionButton from 'modules/ReportModule/components/SendEventConversionButton';
import useWorkspace from 'store/redux/hooks/useWorkspace';
import { DateFormatter } from 'components/charts/formatters';

const { Text } = Typography;

const UserReports = ({ sendConversion, userData }) => {
  const { workspace } = useWorkspace();
  const selected_website_id = localStorage.getItem(`selected_website_id_${workspace.id}`);
  const { user_id, website_id = selected_website_id } = useSearchParams();
  const [getReport, { isLoading, isFetching, data = {} }] = ninjaApi.useLazyGetReportUserQuery();

  useEffect(() => {
    if (user_id && website_id) {
      getReport({ user_id, website_id }).unwrap();
    }
  }, [user_id, website_id, workspace.id]);

  const loading = isLoading || isFetching;
  const user = { ...data?.data, ...userData } || {};

  const items = [
    {
      key: 'os',
      icon: mdiTabletCellphone,
      label: 'OS',
      value: user.os ? (
        <Tooltip title={`Version: ${user.os_version || 'Unknown'}`}>
          <span>{user.os_name || user.os}</span>
        </Tooltip>
      ) : (
        '-'
      ),
    },
    {
      key: 'device',
      icon: mdiCellphone,
      label: 'Device',
      value: user.device || '-',
    },
    {
      key: 'created_at',
      icon: mdiClockOutline,
      label: 'Created At',
      value: <DateFormatter date={user?.created_at} showTime />,
    },
    {
      key: 'browser',
      icon: mdiWeb,
      label: 'Browser',
      value: user.browser ? (
        <Tooltip title={`Version: ${user.browser_version || 'Unknown'}`}>
          <span>{user.browser_name || user.browser}</span>
        </Tooltip>
      ) : (
        '-'
      ),
    },

    { key: 'ip_address', icon: mdiIpNetwork, label: 'Address', value: user.ip_address },
    {
      key: 'first_seen',
      icon: mdiClockOutline,
      label: 'First Seen',
      value: <DateFormatter date={user?.first_seen} showTime />,
    },
    {
      key: 'website',
      icon: mdiCellphone,
      label: 'Website',
      value: user.website ? <JourneyUrlFormatter url={user.website} /> : '-',
    },
    {
      key: 'robot',
      icon: mdiRobotOutline,
      label: 'Robot',
      value: (
        <span
          className={`${
            user.robot == false
              ? 'bg-green text-white'
              : user.robot == false
              ? 'bg-error text-white'
              : 'bg-white'
          }  rounded-md px-1 text-sm`}
        >
          {user.robot == false ? 'NO' : user.robot === true ? 'Yes' : '-'}
        </span>
      ),
    },
    {
      key: 'last_seen',
      icon: mdiClockOutline,
      label: 'Last Seen',
      value: <DateFormatter date={user?.last_seen} showTime />,
    },
    {
      key: 'session_count',
      icon: mdiChartBar,
      label: 'Session Count',
      value: getFormattedNumber(user.session_count),
    },
    {
      key: 'user_agent',
      icon: mdiAccountCogOutline,
      label: 'User Agent',
      value: user.user_agent,
      col: 2,
    },
  ];

  return (
    <div>
      <Flex align='center' justify='space-between' gap='large' wrap>
        {loading ? (
          <Skeleton avatar active size='small' title={false} paragraph={2} />
        ) : (
          <Flex gap='large' align='center'>
            <img src='/icons/anonymous.svg' className='h-10' />
            {(user.name || user.id) && (
              <div>
                <Typography className='text-lg font-medium mb-0.5 w-full whitespace-nowrap overflow-hidden text-ellipsis'>
                  {user.name}
                </Typography>
                <div className='flex items-center gap-1 rounded-md'>
                  <Text className='text-sm font-normal text-gray'>ID:</Text>
                  <Text copyable className='text-sm font-medium'>
                    {user.user_id || '-'}
                  </Text>
                </div>
              </div>
            )}
          </Flex>
        )}

        {/* {sendConversion && <SendEventConversionButton size='medium' type='primary' />} */}
      </Flex>

      <Divider orientation='center'>
        <Flex align='center' className='text-sm font-medium' gap='small'>
          <span>User Properties</span>
          <Icon path={mdiMagnify} size={0.6} />
        </Flex>
      </Divider>

      {loading ? (
        <Skeleton active size='small' title={false} paragraph={2} />
      ) : (
        <div>
          <Card className='border border-solid shadow-sm border-background-light h-fit'>
            <Descriptions
              size='small'
              column={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 3 }}
              labelStyle={{
                fontSize: 14,
                color: '#252d3a',
                minWidth: 40,
                fontWeight: '400',
                alignItems: 'start',
              }}
              contentStyle={{ fontSize: 14, fontWeight: 500 }}
              items={items.map(({ key, icon, label, value, col }) => ({
                key,
                label: (
                  <span className='flex items-center gap-1 font-normal text-color-dark'>
                    <Icon path={icon} size={0.6} /> {label}
                  </span>
                ),
                children: value || '-',
                col,
              }))}
            />
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserReports;
